import { browserAiToolsFilterOptionsQuerySchema, browserAiToolsFilterOptionsResponseSchema, browserAiToolsTableResponseSchema, listBrowserAiToolsDataTableParamsSchema, } from '../contracts/shadow-ai-browser-tools';
import { serializeTableQueryParams } from '../contracts/table';
import { personalShadowAiStatsSchema, } from '../contracts/account-shadow-ai-stats';
import { shadowAiReportRequestSchema, shadowAiToolListResponseSchema, shadowAiToolUsersResponseSchema, runningProcessListResponseSchema, shadowAiOrgStatsSchema, pendingDomainClassificationBodySchema, bulkClassifyDomainsResponseSchema, bulkClassifyDomainsBodySchema, networkConnectionsResponseSchema, networkConnectionUsersResponseSchema, networkConnectionsQuerySchema, networkConnectionsFilterOptionsQuerySchema, networkConnectionsFilterOptionsResponseSchema, networkTrafficStatsSchema, networkDomainsResponseSchema, agentNamesResponseSchema, shadowAiToolListBaseQuerySchema, shadowAiToolListQuerySchema, runningProcessListBaseQuerySchema, runningProcessListQuerySchema, bloomFiltersResponseSchema, bloomFilterChecksumResponseSchema, shadowAiSuccessResponseSchema, shadowAiDomainSchema, shadowAiToolIdSchema, shadowAiTopUsersResponseSchema, shadowAiTopToolsResponseSchema, } from '../contracts/shadow-ai';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
import { runningNowFilterOptionsQuerySchema, runningNowFilterOptionsResponseSchema, runningNowTableResponseSchema, listRunningNowDataTableParamsSchema, } from '../contracts/shadow-ai-running-now';
import { personalRunningNowFilterOptionsQuerySchema, personalRunningNowFilterOptionsResponseSchema, personalRunningNowTableResponseSchema, listPersonalRunningNowDataTableParamsSchema, } from '../contracts/account-shadow-ai-running-now';
import { personalBrowserAiToolsFilterOptionsQuerySchema, personalBrowserAiToolsFilterOptionsResponseSchema, personalBrowserAiToolsTableResponseSchema, listPersonalBrowserAiToolsDataTableParamsSchema, } from '../contracts/account-shadow-ai-browser-tools';
import { installedToolsFilterOptionsQuerySchema, installedToolsFilterOptionsResponseSchema, installedToolsTableResponseSchema, listInstalledToolsDataTableParamsSchema, } from '../contracts/shadow-ai-installed-tools';
import { listPersonalInstalledToolsDataTableParamsSchema, personalInstalledToolsTableResponseSchema, personalInstalledToolsFilterOptionsQuerySchema, personalInstalledToolsFilterOptionsResponseSchema, } from '../contracts/account-shadow-ai-installed-tools';
function serializeParams(params) {
    const { categories, ...rest } = params;
    return {
        ...rest,
        ...(categories?.length ? { categories: categories.join(',') } : {}),
    };
}
export async function listOrganizationShadowAiTools(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(shadowAiToolListQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/tools`, { params: serializeParams(validatedParams) });
    return parseSchema(shadowAiToolListResponseSchema, response);
}
export async function listUserShadowAiTools(client, params = {}) {
    const validatedParams = parseSchema(shadowAiToolListBaseQuerySchema, params);
    const response = await client.get('/account/shadow-ai/tools', {
        params: serializeParams(validatedParams),
    });
    return parseSchema(shadowAiToolListResponseSchema, response);
}
export async function getPersonalShadowAiStats(client) {
    const response = await client.get('/account/shadow-ai/stats');
    return parseSchema(personalShadowAiStatsSchema, response);
}
export async function getShadowAiToolUsers(client, organizationId, toolId, 
/**
 * KIR-3495: narrow the per-tool user list to one org member, so expanding a
 * row on the Shadow AI user-scope page does not reveal the whole org.
 * Honoured only for manager/owner/support (see the route).
 */
scopeUserId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedToolId = parseSchema(shadowAiToolIdSchema, toolId);
    const path = `/organizations/${validatedId}/shadow-ai/tools/${encodeURIComponent(validatedToolId)}/users`;
    // Keep the unscoped call shape untouched — passing an explicit `undefined`
    // config would change the request arity for every existing caller.
    const response = scopeUserId
        ? await client.get(path, { params: { scopeUserId } })
        : await client.get(path);
    return parseSchema(shadowAiToolUsersResponseSchema, response);
}
export async function submitShadowAiReport(client, report) {
    const validatedReport = parseSchema(shadowAiReportRequestSchema, report);
    const response = await client.post('/shadow-ai/report', validatedReport);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
export async function listOrganizationRunningProcesses(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(runningProcessListQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/running`, { params: validatedParams });
    return parseSchema(runningProcessListResponseSchema, response);
}
export async function listUserRunningProcesses(client, params = {}) {
    const validatedParams = parseSchema(runningProcessListBaseQuerySchema, params);
    const response = await client.get('/account/shadow-ai/running', {
        params: validatedParams,
    });
    return parseSchema(runningProcessListResponseSchema, response);
}
export async function getOrgShadowAiStats(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/stats`);
    return parseSchema(shadowAiOrgStatsSchema, response);
}
export async function listOrgNetworkConnections(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(networkConnectionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-connections`, {
        params: validatedParams,
        // Encode per-column `filters` as bracket-notation query params (KIR-3496).
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(networkConnectionsResponseSchema, response);
}
export async function getNetworkConnectionsFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(networkConnectionsFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-connections/filter-options`, { params: validatedParams });
    return parseSchema(networkConnectionsFilterOptionsResponseSchema, response);
}
export async function getNetworkConnectionUsers(client, organizationId, domain) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-connections/${encodeURIComponent(validatedDomain)}/users`);
    return parseSchema(networkConnectionUsersResponseSchema, response);
}
export async function getOrgNetworkStats(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-stats`);
    return parseSchema(networkTrafficStatsSchema, response);
}
export async function listOrgNetworkDomains(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network/domains`);
    const parsed = parseSchema(networkDomainsResponseSchema, response);
    return parsed.domains;
}
export async function getAgentNames(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/agent-names`);
    const parsed = parseSchema(agentNamesResponseSchema, response);
    return parsed.names;
}
export async function getOrgTopShadowAiUsers(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/top-users`, { params });
    return parseSchema(shadowAiTopUsersResponseSchema, response);
}
export async function getOrgTopShadowAiTools(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/top-active-tools`, { params });
    return parseSchema(shadowAiTopToolsResponseSchema, response);
}
export async function exportShadowAiToolsCsv(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/tools/export`, { responseType: 'blob' });
    return response;
}
// Network Discovery / Pending Domains
export async function classifyDomain(client, domain, classification, organizationId, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const validatedBody = parseSchema(pendingDomainClassificationBodySchema, {
        classification,
        ...options,
    });
    const response = await client.post(`/organizations/${validatedId}/shadow-ai/pending-domains/${encodeURIComponent(validatedDomain)}/classify`, validatedBody);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
export async function bulkClassifyDomains(client, domains, classification, organizationId, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(bulkClassifyDomainsBodySchema, {
        domains,
        classification,
        ...options,
    });
    const response = await client.post(`/organizations/${validatedId}/shadow-ai/pending-domains/bulk-classify`, validatedBody);
    return parseSchema(bulkClassifyDomainsResponseSchema, response);
}
// Organization Ignore List
export async function removeOrgIgnoreDomain(client, organizationId, domain) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const response = await client.delete(`/organizations/${validatedId}/shadow-ai/ignore-domains/${encodeURIComponent(validatedDomain)}`);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
// Classified Domains (Sanctioned AI list)
export async function removeClassifiedDomain(client, domain, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const response = await client.delete(`/organizations/${validatedId}/shadow-ai/classified-domains/${encodeURIComponent(validatedDomain)}`);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
/**
 * Get bloom filters for Shadow AI detection
 */
export async function getBloomFilters(client) {
    const response = await client.get('/shadow-ai/bloom-filters');
    return parseSchema(bloomFiltersResponseSchema, response);
}
/**
 * Get bloom filter checksum/version
 */
export async function getBloomFilterChecksum(client) {
    const response = await client.get('/shadow-ai/bloom-filters/checksum');
    return parseSchema(bloomFilterChecksumResponseSchema, response);
}
export async function listBrowserAiToolsDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listBrowserAiToolsDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/browser-ai-tools`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(browserAiToolsTableResponseSchema, response);
}
export async function getBrowserAiToolsFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(browserAiToolsFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/browser-ai-tools/filter-options`, { params: validatedParams });
    return parseSchema(browserAiToolsFilterOptionsResponseSchema, response);
}
export async function listRunningNowDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listRunningNowDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/running-now`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(runningNowTableResponseSchema, response);
}
export async function getRunningNowFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(runningNowFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/running-now/filter-options`, { params: validatedParams });
    return parseSchema(runningNowFilterOptionsResponseSchema, response);
}
/**
 * List the signed-in user's running AI processes (personal, org-agnostic).
 * Response wrapper additionally carries `lastHeartbeat` for AgentStatus.
 */
export async function listPersonalRunningNowDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalRunningNowDataTableParamsSchema, params);
    const response = await client.get('/account/shadow-ai/running-now', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalRunningNowTableResponseSchema, response);
}
/**
 * Distinct filter option values for a personal running-now field
 * (name | category | processUser), scoped to the signed-in user.
 */
export async function getPersonalRunningNowFilterOptions(client, params) {
    const validatedParams = parseSchema(personalRunningNowFilterOptionsQuerySchema, params);
    const response = await client.get('/account/shadow-ai/running-now/filter-options', { params: validatedParams });
    return parseSchema(personalRunningNowFilterOptionsResponseSchema, response);
}
/**
 * List the signed-in user's browser AI tools (personal, org-agnostic).
 * Columns mirror org browser-tools minus the userCount column.
 */
export async function listPersonalBrowserAiToolsDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalBrowserAiToolsDataTableParamsSchema, params);
    const response = await client.get('/account/shadow-ai/browser-ai-tools', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalBrowserAiToolsTableResponseSchema, response);
}
/**
 * Distinct filter option values for a personal browser-ai-tools field
 * (domain | name | category), scoped to the signed-in user.
 */
export async function getPersonalBrowserAiToolsFilterOptions(client, params) {
    const validatedParams = parseSchema(personalBrowserAiToolsFilterOptionsQuerySchema, params);
    const response = await client.get('/account/shadow-ai/browser-ai-tools/filter-options', { params: validatedParams });
    return parseSchema(personalBrowserAiToolsFilterOptionsResponseSchema, response);
}
export async function listInstalledAiToolsDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listInstalledToolsDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/installed-ai-tools`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(installedToolsTableResponseSchema, response);
}
export async function getInstalledAiToolsFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(installedToolsFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/installed-ai-tools/filter-options`, { params: validatedParams });
    return parseSchema(installedToolsFilterOptionsResponseSchema, response);
}
/**
 * List the signed-in user's installed AI tools (personal, org-agnostic). The
 * row drops the org-only `userCount`/`users` column and the wrapper carries no
 * `lastHeartbeat`.
 */
export async function listPersonalInstalledToolsDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalInstalledToolsDataTableParamsSchema, params);
    const response = await client.get('/account/shadow-ai/installed-tools', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalInstalledToolsTableResponseSchema, response);
}
/**
 * Distinct filter option values for a personal installed-tools field
 * (name | category), scoped to the signed-in user.
 */
export async function getPersonalInstalledToolsFilterOptions(client, params) {
    const validatedParams = parseSchema(personalInstalledToolsFilterOptionsQuerySchema, params);
    const response = await client.get('/account/shadow-ai/installed-tools/filter-options', { params: validatedParams });
    return parseSchema(personalInstalledToolsFilterOptionsResponseSchema, response);
}
//# sourceMappingURL=shadow-ai.js.map